import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { לוחבקשותפוסטבידינגBoard } from '@api/BoardSDK';
import { assertRateLimit, checkOtpLockout, clearOtpLockout, recordOtpFailure } from './rateLimit.server';
import { verifyTurnstileToken } from './turnstile.server';
import { lookupCard } from './cancelLookup.server';

/**
 * Self-service cancellation of ONE post-bidding request card on "לוח בקשות פוסט בידינג" (5104802965).
 *
 * Identity is proven in two steps: the student supplies מס. בקשה (column2) + an email, and a 6-digit
 * code is written to "קוד אימות" (column21) — the board's own automation mails it to the address stored
 * ON THE CARD (email), never to the address that was typed in. "תוקף הקוד" (column22) carries the
 * expiry + attempt counter so the code column holds nothing but the code the automation sends.
 *
 * Cancelling writes "בוטל לבקשת הסטודנט" to "סטטוס טיפול בבקשה" (column13) only. "שלב טיפול" (column14)
 * is left untouched for departmental staff to advance manually.
 */
const CLOSED_STATUS_LABELS = ['בוטל', 'בוטל לבקשת הסטודנט', 'הושלם'];
const CANCELLED_REQUEST_STATUS = 'בוטל לבקשת הסטודנט';
const CODE_TTL_MS = 10 * 60 * 1000;
const MAX_ATTEMPTS = 4;

export type CancelStage = 'sent' | 'not_found' | 'email_mismatch' | 'closed' | 'error';

export interface CancelCodeResult {
  stage: CancelStage;
  message: string;
  /** Partially masked so a stranger guessing request numbers never learns the real address. */
  maskedEmail: string | null;
}

export type ConfirmStage = 'cancelled' | 'wrong_code' | 'expired' | 'locked' | 'not_found' | 'closed' | 'error';

export interface ConfirmCancelResult {
  stage: ConfirmStage;
  message: string;
  /** Present only after a successful cancellation. */
  courseName: string | null;
  attemptsLeft: number | null;
}

function maskEmail(email: string): string {
  const [user, domain] = email.split('@');
  if (!user || !domain) return '—';
  const head = user.slice(0, 2);
  return `${head}${'*'.repeat(Math.max(2, user.length - 2))}@${domain}`;
}

function makeCode(): string {
  return String(Math.floor(100000 + Math.random() * 900000));
}

/** "תוקף הקוד" holds both the expiry and the attempt counter, e.g. 2026-09-24T10:12:00.000Z|2 */
function packMeta(expiresAt: number, attempts: number): string {
  return `${new Date(expiresAt).toISOString()}|${attempts}`;
}

function unpackMeta(raw: string | null): { expiresAt: number; attempts: number } {
  const [iso, tries] = (raw ?? '').split('|');
  const expiresAt = iso ? Date.parse(iso) : NaN;
  return { expiresAt: Number.isNaN(expiresAt) ? 0 : expiresAt, attempts: Number(tries) || 0 };
}

function cleanText(val: string | null | undefined): string {
  return (val ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\u200B-\u200D\uFEFF\u200E\u200F]/g, '')
    .replace(/\s+/g, ' ');
}

async function findCard(requestNumber: string, typedEmail?: string) {
  const res = await lookupCard(requestNumber, typedEmail);
  return res.card;
}

const NOT_FOUND_MESSAGE =
  'מספר הבקשה שהוזן לא נמצא. יש לבדוק את המספר שהתקבל במייל האישור. אם הבקשה הוגשה ברגע זה, ייתכן שהיא עדיין בעיבוד — נסו שוב בעוד מספר דקות.';
const CLOSED_MESSAGE = 'בקשה זו כבר הושלמה או בוטלה, ולא ניתן לבטל אותה.';
const ERROR_MESSAGE = 'לא ניתן היה לטפל בבקשה כרגע. נסו שוב בעוד מספר רגעים.';

/**
 * The failing step is logged for diagnosis; the student only ever sees a plain message. A
 * deliberately-thrown error with a Hebrew, user-facing message (e.g. the rate-limit guard) is shown
 * as-is; anything unexpected falls back to the generic message instead of leaking an internal error.
 */
function resolveErrorMessage(error: unknown, step: string): string {
  console.error(`[CANCEL] step "${step}" failed`, error);
  if (error instanceof Error && /[\u0590-\u05FF]/.test(error.message)) {
    return error.message;
  }
  return ERROR_MESSAGE;
}

/**
 * Writes the code + expiry. A combined write is tried first; if the API rejects it, each column is
 * written on its own so a single restricted column cannot block the whole flow silently — and the
 * expiry is written BEFORE the code, since writing the code is what triggers the board's mail
 * automation and the expiry must already be in place when the student receives it.
 */
async function writeCodeColumns(itemId: string, code: string, meta: string): Promise<void> {
  const board = new לוחבקשותפוסטבידינגBoard();

  try {
    await board.item(itemId).update({ column22: meta, column21: code }).execute();
    return;
  } catch (error) {
    console.error('[CANCEL] Combined code write failed, retrying one column at a time', error);
  }

  await board.item(itemId).update({ column22: meta }).execute();
  await board.item(itemId).update({ column21: code }).execute();
}

export const requestCancelCode = createServerFn({ method: 'POST' })
  .validator(
    z.object({
      requestNumber: z.string().min(1),
      email: z.string().min(1),
      turnstileToken: z.string().optional(),
    }),
  )
  .handler(async ({ data }): Promise<CancelCodeResult> => {
    const requestNumber = data.requestNumber.trim();
    const typedEmail = data.email.trim().toLowerCase();
    let step = 'חיפוש הבקשה';

    try {
      assertRateLimit('verification', requestNumber || typedEmail || 'anonymous');

      // Check if this request is currently locked out for 5 minutes
      const lockout = checkOtpLockout(requestNumber);
      if (lockout.locked) {
        const mins = Math.ceil(lockout.lockoutRemainingSec / 60);
        return {
          stage: 'error',
          message: `המערכת נעולה עקב ${MAX_ATTEMPTS} ניסיונות שגויים. נסו שוב בעוד כ-${mins} דקות.`,
          maskedEmail: null,
        };
      }

      if (data.turnstileToken) {
        const turnstileRes = await verifyTurnstileToken(data.turnstileToken);
        if (!turnstileRes.success) {
          return {
            stage: 'error',
            message: 'אימות האבטחה נכשל. אנא נסו שוב.',
            maskedEmail: null,
          };
        }
      }

      const card = await findCard(requestNumber, typedEmail);
      if (!card) return { stage: 'not_found', message: NOT_FOUND_MESSAGE, maskedEmail: null };

      const status = (card.column13 ?? '').trim();
      const stage = (card.column14 ?? '').trim();
      if (CLOSED_STATUS_LABELS.includes(status) || CLOSED_STATUS_LABELS.includes(stage)) {
        return { stage: 'closed', message: CLOSED_MESSAGE, maskedEmail: null };
      }

      const cardEmail = (card.email?.email ?? '').trim();
      if (!cardEmail || cleanText(cardEmail) !== cleanText(typedEmail)) {
        return {
          stage: 'email_mismatch',
          message: 'כתובת המייל אינה תואמת לבקשה זו. יש להזין את הכתובת שאיתה הוגשה הבקשה.',
          maskedEmail: null,
        };
      }

      // Writing the code is what triggers the board's automation to mail it to the card's address.
      step = 'כתיבת קוד האימות';
      await writeCodeColumns(String(card.id), makeCode(), packMeta(Date.now() + CODE_TTL_MS, 0));

      return {
        stage: 'sent',
        message: 'נשלח קוד אימות בן 6 ספרות לכתובת המייל הרשומה בבקשה. הקוד תקף ל-10 דקות.',
        maskedEmail: maskEmail(cardEmail),
      };
    } catch (error) {
      return { stage: 'error', message: resolveErrorMessage(error, step), maskedEmail: null };
    }
  });

export const confirmCancelRequest = createServerFn({ method: 'POST' })
  .validator(z.object({ requestNumber: z.string().min(1), email: z.string().min(1), code: z.string().min(1) }))
  .handler(async ({ data }): Promise<ConfirmCancelResult> => {
    const requestNumber = data.requestNumber.trim();
    const typedEmail = data.email.trim().toLowerCase();
    const typedCode = data.code.trim();
    let step = 'חיפוש הבקשה';

    try {
      assertRateLimit('verification', requestNumber || typedEmail || 'anonymous');

      // Check if locked out for 5 minutes
      const lockout = checkOtpLockout(requestNumber);
      if (lockout.locked) {
        const mins = Math.ceil(lockout.lockoutRemainingSec / 60);
        return {
          stage: 'locked',
          message: `המערכת נעולה עקב ${MAX_ATTEMPTS} ניסיונות שגויים. נסו שוב בעוד כ-${mins} דקות.`,
          courseName: null,
          attemptsLeft: 0,
        };
      }

      const card = await findCard(requestNumber, typedEmail);
      if (!card) {
        return { stage: 'not_found', message: NOT_FOUND_MESSAGE, courseName: null, attemptsLeft: null };
      }

      const status = (card.column13 ?? '').trim();
      const stage = (card.column14 ?? '').trim();
      if (CLOSED_STATUS_LABELS.includes(status) || CLOSED_STATUS_LABELS.includes(stage)) {
        return { stage: 'closed', message: CLOSED_MESSAGE, courseName: null, attemptsLeft: null };
      }

      const cardEmail = (card.email?.email ?? '').trim();
      const storedCode = (card.column21 ?? '').trim();
      const { expiresAt } = unpackMeta(card.column22 ?? null);
      const board = new לוחבקשותפוסטבידינגBoard();

      if (cleanText(cardEmail) !== cleanText(typedEmail) || !storedCode) {
        return {
          stage: 'expired',
          message: 'לא נמצא קוד פעיל לבקשה זו. יש לבקש קוד חדש.',
          courseName: null,
          attemptsLeft: null,
        };
      }

      if (!expiresAt || Date.now() > expiresAt) {
        await board.item(card.id).update({ column21: null, column22: null }).execute();
        return {
          stage: 'expired',
          message: 'תוקף הקוד פג. יש לבקש קוד אימות חדש.',
          courseName: null,
          attemptsLeft: null,
        };
      }

      if (storedCode !== typedCode) {
        const failResult = recordOtpFailure(requestNumber);

        if (failResult.locked) {
          await board.item(card.id).update({ column21: null, column22: null }).execute();
          return {
            stage: 'locked',
            message: 'בוצעו 4 ניסיונות שגויים. מטעמי אבטחה, האפשרות ננעלה ל-5 דקות והקוד בוטל.',
            courseName: null,
            attemptsLeft: 0,
          };
        }

        const left = failResult.remaining;
        return {
          stage: 'wrong_code',
          message:
            left === 1
              ? 'קוד שגוי! אזהרה: נותר ניסיון 1 אחרון לפני נעילת המערכת ל-5 דקות.'
              : `הקוד שהוזן שגוי. נותרו ${left} ניסיונות מתוך ${MAX_ATTEMPTS}.`,
          courseName: null,
          attemptsLeft: left,
        };
      }

      // Verified — clear lockout and cancel THIS card only
      clearOtpLockout(requestNumber);
      step = 'סימון הבקשה כמבוטלת';
      await board.item(card.id).update({
        column13: CANCELLED_REQUEST_STATUS,
      }).execute();
      step = 'ניקוי קוד האימות';
      await board.item(card.id).update({ column21: null, column22: null }).execute();

      return {
        stage: 'cancelled',
        message: 'הבקשה בוטלה. ניתן להגיש בקשה חדשה כעת.',
        courseName: (card.column9 ?? '').trim() || null,
        attemptsLeft: null,
      };
    } catch (error) {
      return { stage: 'error', message: resolveErrorMessage(error, step), courseName: null, attemptsLeft: null };
    }
  });
